import React, { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { DEPT_LABEL } from "@/components/InventoryItems";
import HousekeepingStock from "@/pages/HousekeepingStock";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { MONTHS_EL } from "@/lib/format";
import { toast } from "@/components/ui/sonner";
import { Plus, Save, CheckCircle2, Search, Scale } from "lucide-react";

const YEAR = new Date().getFullYear();
const field = "rounded-xl border border-[#E3DED4] bg-white px-3 py-2 text-sm outline-none focus:border-[#45423C]";

function CountSheet({ id, onDone }) {
  const qc = useQueryClient();
  const [qty, setQty] = useState({});
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const { data: c } = useQuery({ queryKey: ["inv-count", id], queryFn: async () => (await api.get(`/inventory/counts/${id}`)).data });
  const rows = c?.rows || [];
  const cats = useMemo(() => Array.from(new Set(rows.map((r) => r.category).filter(Boolean))).sort(), [rows]);
  const shown = rows.filter((r) => (!cat || r.category === cat) && (!q || (r.name || "").toLowerCase().includes(q.toLowerCase())));
  const locked = c?.status === "closed";
  const save = useMutation({
    mutationFn: async () => api.put(`/inventory/counts/${id}`, { rows: Object.entries(qty).map(([item_id, v]) => ({ item_id, qty: Number(v) || 0 })) }),
    onSuccess: () => { setQty({}); qc.invalidateQueries({ queryKey: ["inv-count", id] }); toast.success("Οι ποσότητες αποθηκεύτηκαν"); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const submit = useMutation({
    mutationFn: async () => {
      if (Object.keys(qty).length) await save.mutateAsync();
      return api.post(`/inventory/counts/${id}/submit`);
    },
    onSuccess: () => { toast.success("Η καταμέτρηση ολοκληρώθηκε και στάλθηκε στη διεύθυνση"); qc.invalidateQueries({ queryKey: ["inv-staff-list"] }); onDone(); },
    onError: (e) => toast.error(apiErr(e)),
  });
  if (!c) return <div className="text-sm text-[#6F6A60]">Φόρτωση…</div>;
  return (
    <div className="rounded-2xl bg-white border border-[#E3DED4] overflow-hidden" data-testid="staff-count-sheet">
      <div className="px-4 py-3 border-b border-[#E3DED4] flex flex-wrap items-center gap-2">
        <h2 className="font-medium text-[#45423C] mr-2">{MONTHS_EL[c.month]} {c.year}{c.submitted ? " · ολοκληρώθηκε" : ""}</h2>
        <select data-testid="staff-count-category" value={cat} onChange={(e) => setCat(e.target.value)} className={field}>
          <option value="">Όλες οι κατηγορίες</option>{cats.map((x) => <option key={x} value={x}>{x}</option>)}
        </select>
        <div className="relative">
          <Search className="h-4 w-4 text-[#6F6A60] absolute left-3 top-1/2 -translate-y-1/2" />
          <input data-testid="staff-count-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Αναζήτηση" className={`${field} pl-9 w-40`} />
        </div>
      </div>
      <div className="max-h-[60vh] overflow-y-auto divide-y divide-[#E3DED4]">
        {shown.map((r) => (
          <div key={r.item_id} className="flex items-center gap-3 px-4 py-2" data-testid={`staff-row-${r.item_id}`}>
            <div className="flex-1 min-w-0"><div className="text-sm font-medium text-[#45423C] truncate">{r.name}</div><div className="text-[11px] text-[#8A8577]">{r.category || "—"}</div></div>
            <input data-testid={`staff-qty-${r.item_id}`} type="number" inputMode="decimal" step="0.01" disabled={locked}
              value={qty[r.item_id] ?? r.qty} onChange={(e) => setQty({ ...qty, [r.item_id]: e.target.value })}
              className="w-20 rounded-lg border border-[#E3DED4] px-2 py-1.5 text-right text-sm tabular disabled:bg-[#F7F5F1]" />
            <span className="w-10 text-xs text-[#6F6A60]">{r.unit}</span>
          </div>
        ))}
        {shown.length === 0 && <div className="px-4 py-6 text-sm text-[#6F6A60]">Κανένα είδος.</div>}
      </div>
      {!locked && (
        <div className="p-3 border-t border-[#E3DED4] grid grid-cols-2 gap-2">
          <button data-testid="staff-count-save" onClick={() => save.mutate()} disabled={save.isPending || !Object.keys(qty).length}
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-[#E3DED4] py-2.5 text-sm font-semibold disabled:opacity-40"><Save className="h-4 w-4" /> Αποθήκευση</button>
          <button data-testid="staff-count-submit" onClick={() => { if (window.confirm("Ολοκλήρωση καταμέτρησης;")) submit.mutate(); }} disabled={submit.isPending}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#6E7A63] text-white py-2.5 text-sm font-semibold disabled:opacity-40"><CheckCircle2 className="h-4 w-4" /> Ολοκλήρωση</button>
        </div>
      )}
    </div>
  );
}

export default function StaffStockCount() {
  const qc = useQueryClient();
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [openId, setOpenId] = useState(null);
  const [params] = useSearchParams();
  const dept = params.get("dept") || "fnb";
  const [mode, setMode] = useState(params.get("mode") === "quick" ? "quick" : "monthly");
  const { data } = useQuery({ queryKey: ["inv-staff-list", YEAR, dept], queryFn: async () => (await api.get(`/inventory/counts?year=${YEAR}&department=${dept}`)).data });
  const counts = data?.rows || [];
  const create = useMutation({
    mutationFn: async () => (await api.post("/inventory/counts", { year: YEAR, month, department: dept })).data,
    onSuccess: (d) => { setOpenId(d.id); qc.invalidateQueries({ queryKey: ["inv-staff-list"] }); toast.success("Η καταμέτρηση ξεκίνησε"); },
    onError: (e) => toast.error(apiErr(e)),
  });
  return (
    <div className="space-y-5 max-w-3xl" data-testid="staff-stock-page">
      <h1 className="font-serif text-3xl md:text-4xl text-[#45423C] inline-flex items-center gap-2"><Scale className="h-7 w-7" /> Καταμέτρηση αποθήκης · {DEPT_LABEL[dept]}</h1>
      {dept === "housekeeping" && (
        <div className="inline-flex rounded-xl border border-[#E3DED4] overflow-hidden" data-testid="hk-count-mode">
          {[["monthly", "Μηνιαία απογραφή"], ["quick", "Γρήγορη καταμέτρηση (φωτό)"]].map(([k, l]) => (
            <button key={k} data-testid={`hk-count-mode-${k}`} onClick={() => setMode(k)} className={`px-4 py-2 text-sm font-medium ${mode === k ? "bg-[#45423C] text-white" : "bg-white text-[#45423C]"}`}>{l}</button>
          ))}
        </div>
      )}
      {mode === "quick" ? <HousekeepingStock embedded /> : <>
      <div className="rounded-2xl bg-white border border-[#E3DED4] p-4 flex flex-wrap items-center gap-2">
        <select data-testid="staff-count-month" value={month} onChange={(e) => setMonth(Number(e.target.value))} className={field}>
          {MONTHS_EL.slice(1).map((m, i) => <option key={m} value={i + 1}>{m} {YEAR}</option>)}
        </select>
        <button data-testid="staff-count-start" onClick={() => create.mutate()} disabled={create.isPending}
          className="inline-flex items-center gap-2 rounded-xl bg-[#45423C] text-white px-4 py-2 text-sm font-semibold disabled:opacity-50"><Plus className="h-4 w-4" /> Έναρξη καταμέτρησης</button>
      </div>
      <div className="flex flex-wrap gap-2" data-testid="staff-count-list">
        {counts.map((c) => (
          <button key={c.id} data-testid={`staff-count-open-${c.id}`} onClick={() => setOpenId(c.id)}
            className={`rounded-full px-3 py-1.5 text-sm border ${openId === c.id ? "bg-[#45423C] text-white border-[#45423C]" : "bg-white border-[#E3DED4] text-[#45423C]"}`}>
            {MONTHS_EL[c.month]} · {c.status === "closed" ? "κλειστή" : c.submitted ? "ολοκληρώθηκε" : "σε εξέλιξη"}
          </button>
        ))}
      </div>
      {openId && <CountSheet key={openId} id={openId} onDone={() => setOpenId(null)} />}
      </>}
    </div>
  );
}
